/**
 * WelcomeScreen component
 *
 * Personalized welcome screen with random suggestion chips from i18n.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { User, NatalChart } from '../../types';

interface WelcomeScreenProps {
  user: User | null;
  primaryChart: NatalChart | null;
  onSuggest: (text: string) => void;
}

// Category keys for suggestion categories
const SUGGESTION_CATEGORY_KEYS = [
  'natal', 'love', 'synastry', 'transits', 'solar',
  'progressions', 'location', 'career', 'money',
  'health', 'lifestyle', 'family', 'travel'
] as const;

type SuggestionCategory = typeof SUGGESTION_CATEGORY_KEYS[number];

// Pick N random items from different categories
function getRandomSuggestions(
  categories: Record<SuggestionCategory, string[]>,
  count: number = 6
): string[] {
  const shuffledKeys = [...SUGGESTION_CATEGORY_KEYS].sort(() => Math.random() - 0.5);

  const result: string[] = [];
  for (let i = 0; i < Math.min(count, shuffledKeys.length); i++) {
    const categoryKey = shuffledKeys[i];
    const items = categories[categoryKey];
    if (items && items.length > 0) {
      const randomItem = items[Math.floor(Math.random() * items.length)];
      result.push(randomItem);
    }
  }

  return result;
}

function formatBirthDate(dateStr: string): string {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  return `${d}.${m}.${y}`;
}

function formatSignWithDegree(sign?: string, degree?: string): string {
  if (!sign) return '';
  if (!degree) return sign;
  return `${sign} ${degree}`;
}

export function WelcomeScreen({ user, primaryChart, onSuggest }: WelcomeScreenProps) {
  const { t } = useTranslation();
  const firstName = user?.name?.split(' ')[0] || '';

  // Get suggestion categories from translations
  const suggestionCategories = useMemo(() => {
    const categories: Record<SuggestionCategory, string[]> = {} as Record<SuggestionCategory, string[]>;
    for (const key of SUGGESTION_CATEGORY_KEYS) {
      const items = t(`welcome.suggestionCategories.${key}`, { returnObjects: true });
      categories[key] = Array.isArray(items) ? items : [];
    }
    return categories;
  }, [t]);

  // Generate random suggestions once per mount
  const suggestions = useMemo(
    () => getRandomSuggestions(suggestionCategories, 6),
    [suggestionCategories]
  );

  if (primaryChart) {
    // User has a saved chart - show personalized dashboard
    return (
      <div className="welcome" data-testid="welcome-with-chart">
        <h1>{t('welcome.greeting')}{firstName ? `, ${firstName}` : ''}!</h1>
        <div className="user-chart-card">
          <div className="chart-big-three">
            <div className="big-three-item">
              <span className="symbol">&#9737;</span>
              <span className="sign">{formatSignWithDegree(primaryChart.sun_sign, primaryChart.sun_degree)}</span>
            </div>
            <div className="big-three-item">
              <span className="symbol">&#9789;</span>
              <span className="sign">{formatSignWithDegree(primaryChart.moon_sign, primaryChart.moon_degree)}</span>
            </div>
            <div className="big-three-item">
              <span className="symbol">ASC</span>
              <span className="sign">{formatSignWithDegree(primaryChart.asc_sign, primaryChart.asc_degree)}</span>
            </div>
          </div>
          <div className="chart-meta">
            {primaryChart.birth_place} &middot; {formatBirthDate(primaryChart.birth_date)}
          </div>
        </div>

        <div className="suggestions-section">
          <p className="suggestions-title">{t('welcome.tryIt')}</p>
          <div className="suggestions-scroll">
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                className="suggestion-chip"
                onClick={() => onSuggest(suggestion)}
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // No chart - prompt for birth data
  return (
    <div className="welcome" data-testid="welcome-no-chart">
      <h1>{t('welcome.greeting')}!</h1>
      <p>
        {t('welcome.intro')} {t('welcome.withoutChart')}
      </p>
      <div
        className="onboarding-example"
        onClick={() => onSuggest(t('welcome.withoutChartExample'))}
        style={{ cursor: 'pointer' }}
      >
        &quot;{t('welcome.withoutChartExample')}&quot;
      </div>
      <p style={{ marginTop: '20px', fontSize: '0.9em', color: '#888' }}>
        {t('welcome.withoutChartTip')}
      </p>
    </div>
  );
}

export default WelcomeScreen;
